export type PackageItineraryDay = {
  day: string;
  title: string;
  details: string;
  stay?: string;
  meals?: string;
  image?: string | null;
};

export type PackageFeature = {
  iconName?: string;
  description: string;
};

export type PackageOption = {
  id: string;
  title: string;
  destination?: string;
  duration: string;
  price: string;
  idealFor: string;
  summary: string;
  highlights: string[];
  inclusions: Array<string | { iconName: string; description: string }>;
  features?: PackageFeature[];
  priceNotes?: string[];
  itinerary?: PackageItineraryDay[];
  exclusions?: string[];
};

export type PackageEnquirySummary = {
  destinationName: string;
  packageTitle: string;
  duration: string;
  price: string;
  idealFor: string;
  travellers: string;
  travelMonth: string;
  notes: string;
};

export type EnquiryPrefill = {
  subject: string;
  message: string;
  packageSummary: PackageEnquirySummary;
};

export type GenericPackageCard = {
  id: string;
  title: string;
  location: string;
  duration: string;
  price: string;
  rating: number;
  reviews: number;
  tag?: string;
  keyHighlights: string[];
};

export type PackageGroupDestinationId = string;

export type PackageListRouteGroup = {
  routeId: string;
  destinationId: PackageGroupDestinationId;
  destinationName: string;
  title: string;
  subtitle: string;
  cards: GenericPackageCard[];
  tabs: Array<{
    id: string;
    label: string;
    routeId: string;
  }>;
};

export type UnifiedPackageDetail = {
  id: string;
  destinationId: PackageGroupDestinationId;
  title: string;
  collectionTitle: string;
  collectionRouteId: string;
  location: string;
  duration: string;
  transfer: string;
  startingPricePerPersonAed: number;
  rating: number;
  reviews: number;
  tag: string;
  summary: string;
  destinationOverview: string;
  highlights: string[];
  itinerary: PackageItineraryDay[];
  inclusions: string[];
  exclusions: string[];
  paymentTerms: string[];
  cancellationPolicy: string[];
  termsAndConditions: string[];
  importantNotes: string[];
  visaRequirements: string[];
};

const toSlug = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const formatTravelMonth = (value: string) => {
  if (!value) {
    return "Flexible dates";
  }

  const [year, month] = value.split("-");
  const parsedYear = Number(year);
  const parsedMonth = Number(month);

  if (!parsedYear || !parsedMonth) {
    return value;
  }

  return new Date(parsedYear, parsedMonth - 1, 1).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
};

const buildGenericDestinationPackages = (destinationName: string): PackageOption[] => {
  const destinationSlug = toSlug(destinationName);

  return [
    {
      id: `${destinationSlug}-essential`,
      title: `${destinationName} Essential Escape`,
      duration: "4 Days / 3 Nights",
      price: "From $1,299",
      idealFor: "Couples and first-time visitors",
      summary: `A refined short break in ${destinationName} with premium stays, smooth transfers, and the destination's most-loved experiences.`,
      highlights: [
        `Private airport transfers in ${destinationName}`,
        "Handpicked boutique or luxury Accommodation",
        "One guided city or island discovery experience",
      ],
      inclusions: [
        "Daily breakfast",
        "Airport pick-up and drop-off",
        "Sightseeing with local support",
      ],
    },
    {
      id: `${destinationSlug}-signature`,
      title: `${destinationName} Signature Journey`,
      duration: "6 Days / 5 Nights",
      price: "From $2,199",
      idealFor: "Families and luxury holiday planners",
      summary: `A balanced itinerary that combines iconic highlights, leisure time, and elevated hospitality for a complete ${destinationName} experience.`,
      highlights: [
        "Premium room upgrades where available",
        "Curated full-day excursion",
        "Concierge assistance before and during travel",
      ],
      inclusions: [
        "Breakfast and one signature dinner",
        "Private transfers",
        "Curated activity planning",
      ],
    },
    {
      id: `${destinationSlug}-ultra-luxury`,
      title: `${destinationName} Ultra Luxury Retreat`,
      duration: "8 Days / 7 Nights",
      price: "From $3,499",
      idealFor: "Honeymooners and bespoke luxury travellers",
      summary: `An indulgent escape designed around privacy, elevated dining, and premium experiences across the very best of ${destinationName}.`,
      highlights: [
        "Luxury resort or premium suite stay",
        "Private experience with dedicated support",
        "Flexible itinerary with add-on upgrades",
      ],
      inclusions: [
        "Breakfast and select premium experiences",
        "VIP arrival support",
        "Bespoke itinerary consultation",
      ],
    },
  ];
};

export const allPackageRouteTabs: Array<{
  id: string;
  label: string;
  routeId: string;
}> = [];

export const DEFAULT_PACKAGES_ROUTE_ID = "";

export const getPackageListRouteGroupById = (
  _routeId: string,
): PackageListRouteGroup | undefined => undefined;

export const getUnifiedPackageDetailById = (
  _id: string,
): UnifiedPackageDetail | undefined => undefined;

export function buildDestinationPackages(destinationName: string): PackageOption[] {
  return buildGenericDestinationPackages(destinationName);
}

export function buildPackageEnquiryMessage(
  summary: PackageEnquirySummary,
) {
  const lines = [
    `Destination: ${summary.destinationName}`,
    `Selected Package: ${summary.packageTitle}`,
    `Duration: ${summary.duration}`,
    `Price Guide: ${summary.price}`,
    `Ideal For: ${summary.idealFor}`,
    `Travellers: ${summary.travellers}`,
    `Travel Month: ${formatTravelMonth(summary.travelMonth)}`,
  ];

  if (summary.notes.trim()) {
    lines.push(`Additional Notes: ${summary.notes.trim()}`);
  }

  return lines.join("\n");
}
